import { GeckoComponentCoords } from 'types/geometric/geckoComponentCoords'
import { GeckoComponentRect } from 'types/geometric/geckoComponentRect'
import { GeckoComponentSpace } from 'types/geometric/geckoComponentSpace'
import { GeckoMarkupIcon } from 'types/markup/geckoMarkupIcon'
import { GeckoMarkupLabel } from 'types/markup/geckoMarkupLabel'
import { GeckoMarkupLine } from 'types/markup/geckoMarkupLine'
import { GeckoMarkupPolygon } from 'types/markup/geckoMarkupPolygon'
import { GeckoMarkupPrimitive } from 'types/markup/geckoMarkupPrimitive'
import { GeckoMarkupRect } from 'types/markup/geckoMarkupRect'
import {
  parseArray,
  parseDate,
  parseNumber,
  parseString,
} from 'utils/parsingHelpers'
import { compareAlphanumeric } from 'utils/stringHelpers'

import { GeckoEntryBase } from './geckoEntry'
import { RolodexEntryJson, RolodexEntryType } from './rolodexEntryFields'

/**
 * An entry containing a set of 2D shapes and various forms of content
 * (text, icons, etc.) to be projected onto a component in 3D space. Markup
 * layer examples include repair plans, schematics, corrosion labels, etc.
 */
export class GeckoMarkupLayer extends GeckoEntryBase<
  {
    name: string
    color: string | undefined
    timestamp: Date | undefined
    path: ReadonlyArray<string> | undefined
    geometry: GeckoComponentSpace
    // Primitives
    primitives: ReadonlyMap<string, GeckoMarkupPrimitive>
    allPrimitives: ReadonlyArray<GeckoMarkupPrimitive>
    iconPrimitives: ReadonlyArray<GeckoMarkupIcon>
    labelPrimitives: ReadonlyArray<GeckoMarkupLabel>
    linePrimitives: ReadonlyArray<GeckoMarkupLine>
    polygonPrimitives: ReadonlyArray<GeckoMarkupPolygon>
    rectPrimitives: ReadonlyArray<GeckoMarkupRect>
    // Versions
    schema_version: number
  },
  {
    organization: string
    site: string
    unit: string
    component: string
    inspection: string
    [RolodexEntryType.REPAIR_PLAN]?: string
    [RolodexEntryType.LINE_AVERAGE_MARKUP_LAYER]?: string
  }
> {
  //----------------------------------------------------------------------------

  /**
   * A simple constructor, private so that a more descriptive factory method
   * (which can return undefined) is used instead
   * @param json The raw JSON to create an instance from
   * @private
   */
  private constructor(json: RolodexEntryJson) {
    //--------------------------------------------------------------------------

    const name = parseString(json.data.name)
    const path = parseArray<string>(json.data.path)
    const timestamp = parseDate(json.data.createdon) as Date
    const schema_version = parseNumber(json.data.schema_version) ?? -1

    const componentID = json.tags.component ?? ''
    const geometryJSON = json.data.geometry as Record<string, unknown>
    const geometry = GeckoComponentSpace.CreateFromJSON(
      componentID,
      geometryJSON
    )

    if (!name) throw new Error(`Markup layer "${json.id}" missing name`)
    if (!geometry)
      throw new Error(
        `Markup layer geometry malformed: ${JSON.stringify(geometryJSON)}`
      )

    // Extract all the primitives in this markup layer
    const primitives = new Map<string, GeckoMarkupPrimitive>()
    const primitivesJSON =
      (json.data.primitives as
        | Record<string, Record<string, unknown>>
        | undefined) ?? {}

    const allPrimitives = new Array<GeckoMarkupPrimitive>()
    const iconPrimitives = new Array<GeckoMarkupIcon>()
    const labelPrimitives = new Array<GeckoMarkupLabel>()
    const linePrimitives = new Array<GeckoMarkupLine>()
    const polygonPrimitives = new Array<GeckoMarkupPolygon>()
    const rectPrimitives = new Array<GeckoMarkupRect>()

    Object.entries(primitivesJSON).forEach(([key, value]) => {
      if (!value) return
      if (primitives.has(key))
        throw new Error(
          `Duplicate primitive "${key}" in markup layer ${json.id}`
        )
      try {
        const primitive = createPrimitive(key, json.id, value, geometry)
        if (primitive) {
          primitives.set(key, primitive)
          allPrimitives.push(primitive)
          if (primitive instanceof GeckoMarkupIcon)
            iconPrimitives.push(primitive)
          if (primitive instanceof GeckoMarkupLabel)
            labelPrimitives.push(primitive)
          if (primitive instanceof GeckoMarkupLine)
            linePrimitives.push(primitive)
          if (primitive instanceof GeckoMarkupPolygon)
            polygonPrimitives.push(primitive)
          if (primitive instanceof GeckoMarkupRect)
            rectPrimitives.push(primitive)
        }
      } catch (error) {
        console.error(`Markup layer "${json.id} had error "`, error)
      }
    })

    super(
      json,
      {
        name,
        path,
        timestamp,
        color: parseString(json.data.color),
        geometry,
        // Primitives
        primitives,
        allPrimitives,
        iconPrimitives,
        labelPrimitives,
        linePrimitives,
        polygonPrimitives,
        rectPrimitives,
        schema_version,
      },
      {
        organization: parseString(json.tags.organization) ?? '',
        site: parseString(json.tags.site) ?? '',
        unit: parseString(json.tags.unit) ?? '',
        component: parseString(json.tags.component) ?? '',
        inspection: parseString(json.tags.inspection) ?? '',
        [RolodexEntryType.REPAIR_PLAN]: parseString(
          json.tags[RolodexEntryType.REPAIR_PLAN]
        ),
        [RolodexEntryType.LINE_AVERAGE_MARKUP_LAYER]: parseString(
          json.tags[RolodexEntryType.LINE_AVERAGE_MARKUP_LAYER]
        ),
      },
      {}
    )
  }

  /**
   * A simple factory method which creates a markup layer from raw JSON
   * @param json The raw JSON to create an instance from
   * @constructor
   */
  public static CreateFromJSON(json: RolodexEntryJson) {
    return new GeckoMarkupLayer(json)
  }

  /**
   * A simple comparison method, used to sort entries by their names, properly
   * taking into account multi-digit numbers (i.e. 2 will appear before 20)
   * @param a     The first entry
   * @param b     The second entry
   */
  public static DefaultSort(a: GeckoMarkupLayer, b: GeckoMarkupLayer) {
    return compareAlphanumeric(a.data.name, b.data.name)
  }

  //----------------------------------------------------------------------------

  /**
   * Returns whether a primitive can be added/patched to this markup layer
   * @param primitive         The primitive to compare to the existing set
   * @returns boolean indicating whether the markup position is valid
   */
  public isValidPosition(primitive: GeckoMarkupPrimitive) {
    if (primitive instanceof GeckoMarkupLabel) {
      return !this.data.allPrimitives.some(
        (sibling) =>
          sibling instanceof GeckoMarkupLabel &&
          sibling.id !== primitive.id &&
          GeckoComponentCoords.Equals(
            sibling.bounds.start,
            primitive.bounds.end
          )
      )
    }
    if (primitive instanceof GeckoMarkupRect) {
      return !this.data.allPrimitives.some(
        (sibling) =>
          sibling instanceof GeckoMarkupRect &&
          sibling.id !== primitive.id &&
          GeckoComponentRect.Equals(sibling.bounds, primitive.bounds)
      )
    }
    return true
  }
}

/**
 * A simple, local factory method for creating a primitive from JSON
 * @param primitiveID       The ID of the primitive to create
 * @param markupLayerID     The ID of the markup layer this primitive belongs to
 * @param json              The JSON to create this primitive from
 * @param geometry          The markup layer's measurement units
 */
const createPrimitive = (
  primitiveID: string,
  markupLayerID: string,
  json: Record<string, unknown>,
  geometry: GeckoComponentSpace
) => {
  const primitiveKind = parseString(json.kind)
  switch (primitiveKind) {
    case 'icon':
      return GeckoMarkupIcon.CreateFromJSON(
        primitiveID,
        markupLayerID,
        json,
        geometry
      )
    case 'label':
      return GeckoMarkupLabel.CreateFromJSON(
        primitiveID,
        markupLayerID,
        json,
        geometry
      )
    case 'rect':
      return GeckoMarkupRect.CreateFromJSON(
        primitiveID,
        markupLayerID,
        json,
        geometry
      )
    case 'polygon':
      return GeckoMarkupPolygon.CreateFromJSON(
        primitiveID,
        markupLayerID,
        json,
        geometry
      )
    case 'line':
      return GeckoMarkupLine.CreateFromJSON(
        primitiveID,
        markupLayerID,
        json,
        geometry
      )
    default:
      throw new Error(`Unknown primitive kind "${primitiveKind}"`)
  }
}
